import { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaShoppingCart, FaSignOutAlt, FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CartIcon } from '../cart';
import UserMenu from './UserMenu';
import AuthModal from '../auth/AuthModal';
import HeaderSearch from './HeaderSearch';

export default function Navbar({ onCartClick, onAuthModalOpen }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  // En móvil el buscador se despliega bajo la barra: no cabe junto al logo y
  // el carrito sin comerse el espacio de toque de ambos.
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { user, logout, isAuthenticated } = useAuth();

  const links = [
    { name: 'Inicio', path: '/' },
    { name: 'Tienda', path: '/productos' },
    { name: 'Ahí les dejo', path: '/ahilesdejo' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  const handleCartClick = () => {
    onCartClick();
    setIsMenuOpen(false);
  };

  return (
    <nav
      className={`fixed w-full z-40 transition-all duration-300 ${
        isScrolled
          ? 'bg-df-bg/95 backdrop-blur-md shadow-lg shadow-black/20 border-b border-df-border/50'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center navbar-height">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" style={{ gap: '0px' }}>
            <div className="h-28 w-28 md:h-32 md:w-32 overflow-hidden flex-shrink-0 -my-4 -mr-10">
              <img
                src="/logo-dark.webp"
                alt="Tablero Infinito"
                className="h-full w-full object-contain"
              />
            </div>
            <span className="font-cinzel text-sm md:text-base font-bold text-df-text hidden sm:block">
              Tablero Infinito
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            {links.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="px-4 py-2 text-sm font-medium text-df-muted hover:text-df-accent rounded-lg transition-colors duration-200"
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Buscador de escritorio */}
          <div className="hidden md:block flex-1 max-w-sm mx-4">
            <HeaderSearch />
          </div>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-4">
            <UserMenu
              onAuthModalOpen={onAuthModalOpen}
              isAuthenticated={isAuthenticated}
              user={user}
              onLogout={handleLogout}
              isScrolled={isScrolled}
            />
            <CartIcon onClick={handleCartClick} />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={() => {
                setIsSearchOpen(!isSearchOpen);
                setIsMenuOpen(false);
              }}
              aria-label={isSearchOpen ? 'Cerrar buscador' : 'Buscar juegos'}
              aria-expanded={isSearchOpen}
              className="text-df-muted hover:text-df-accent focus:outline-none transition-colors"
            >
              <FaSearch className="h-5 w-5" />
            </button>
            <CartIcon onClick={handleCartClick} />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-df-muted hover:text-df-accent focus:outline-none transition-colors"
            >
              {isMenuOpen ? <FaTimes className="h-5 w-5" /> : <FaBars className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Buscador móvil */}
      {isSearchOpen && (
        <div className="md:hidden bg-df-surface/98 backdrop-blur-md border-t border-df-border px-4 py-3">
          <HeaderSearch autoFocus onNavigate={() => setIsSearchOpen(false)} />
        </div>
      )}

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-df-surface/98 backdrop-blur-md border-t border-df-border shadow-xl">
          <div className="px-4 py-4 space-y-1">
            {links.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium text-df-muted hover:text-df-accent hover:bg-df-surface2 rounded-lg transition-colors"
              >
                {link.name}
              </Link>
            ))}

            <div className="border-t border-df-border pt-3 mt-3">
              {isAuthenticated ? (
                <div className="space-y-1">
                  <div className="px-4 py-2">
                    <span className="text-xs text-df-muted">
                      Hola, {user?.name || user?.email}
                    </span>
                  </div>
                  <Link
                    to="/perfil"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-3 text-sm font-medium text-df-muted hover:text-df-accent hover:bg-df-surface2 rounded-lg transition-colors"
                  >
                    Mi Perfil
                  </Link>
                  <Link
                    to="/pedidos"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-3 text-sm font-medium text-df-muted hover:text-df-accent hover:bg-df-surface2 rounded-lg transition-colors"
                  >
                    Mis Pedidos
                  </Link>
                  {user?.role_id && user.role_id <= 2 && (
                    <Link
                      to="/admin"
                      onClick={() => setIsMenuOpen(false)}
                      className="block px-4 py-3 text-sm font-medium text-df-cyan hover:bg-df-surface2 rounded-lg transition-colors"
                    >
                      Panel Administrativo
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-4 py-3 text-sm font-medium text-df-danger hover:bg-df-surface2 rounded-lg transition-colors"
                  >
                    <FaSignOutAlt className="text-xs" />
                    Cerrar sesión
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      onAuthModalOpen('login');
                      setIsMenuOpen(false);
                    }}
                    className="block w-full px-4 py-3 text-sm font-medium text-df-muted hover:text-df-accent hover:bg-df-surface2 rounded-lg transition-colors text-left"
                  >
                    Iniciar Sesión
                  </button>
                  <button
                    onClick={() => {
                      onAuthModalOpen('register');
                      setIsMenuOpen(false);
                    }}
                    className="block w-full px-4 py-3 text-sm font-medium bg-df-accent-strong hover:bg-df-accent-deep text-white rounded-lg transition-colors text-center"
                  >
                    Registrarse
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auth modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
    </nav>
  );
}
